import {useEffect, useState} from "react";
import {Plus, ArrowUpRight, BookOpen, Save, X, Edit3, Trash2} from "lucide-react";
import {apiGet, apiPost, apiPut, apiDelete} from "@/lib/api";
import {FormModal} from "@/components/UI";

export default function Blog({notify, user, setActive}) {
  const [posts, setPosts] = useState([]);
  const [active, setActive_] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { apiGet("/blog").then(setPosts).catch(() => setPosts([])).finally(() => setLoading(false)); }, []);

  const openPost = (p) => apiGet(`/blog/${p.slug}`).then(setActive_).catch(() => notify("Could not open post"));

  return (
    <div data-testid="blog-page">
      <div className="page-title">
        <div>
          <div className="eyebrow">THE JOURNAL</div>
          <h1>Stories<br/><em>from the Ocean.</em></h1>
          <p>Essays, interviews and dispatches — curated by our editors for the Indian diaspora everywhere.</p>
        </div>
      </div>

      {active ? (
        <article className="blog-article" data-testid="blog-article">
          <button data-testid="blog-back" className="back-btn" onClick={() => setActive_(null)}>← Back to all stories</button>
          {active.cover_image && <img src={active.cover_image} alt={active.title} className="blog-cover"/>}
          <div className="eyebrow">{(active.tags || []).join(" · ") || "STORY"}</div>
          <h1>{active.title}</h1>
          <p className="blog-byline">By {active.author} · {new Date(active.created_at).toLocaleDateString()}</p>
          <div className="blog-body" dangerouslySetInnerHTML={{__html: active.body}}/>
        </article>
      ) : loading ? (
        <div className="empty-pane"><p>Loading stories…</p></div>
      ) : posts.length === 0 ? (
        <div className="empty-pane"><BookOpen size={25}/><h3>No stories yet</h3><p>Our editors are putting the first issue together. Check back soon.</p></div>
      ) : (
        <div className="blog-grid">
          {posts.map(p => (
            <article key={p.post_id} className="blog-card" data-testid={`blog-card-${p.post_id}`} onClick={() => openPost(p)}>
              {p.cover_image && <img src={p.cover_image} alt={p.title} loading="lazy"/>}
              <div className="blog-card-body">
                <div className="eyebrow">{(p.tags || []).slice(0, 2).join(" · ") || "STORY"}</div>
                <h3>{p.title}</h3>
                <p>{p.excerpt}</p>
                <span className="blog-card-foot">
                  <small>By {p.author} · {new Date(p.created_at).toLocaleDateString()}</small>
                  <ArrowUpRight size={16}/>
                </span>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

// Admin-side editor exported for use from Admin studio
export function BlogEditor({notify, close, post = null, onSaved}) {
  const [f, setF] = useState(post || {title: "", excerpt: "", body: "", cover_image: "", tags: "", published: true, slug: ""});
  const ch = (e) => setF({...f, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value});

  const submit = async (e) => {
    e.preventDefault();
    const payload = {...f, tags: Array.isArray(f.tags) ? f.tags : f.tags.split(/[\s,]+/).filter(Boolean)};
    try {
      const r = post ? await apiPut(`/blog/${post.post_id}`, payload) : await apiPost("/blog", payload);
      onSaved?.(r);
      notify(post ? "Post updated" : "Post published");
      close();
    } catch { notify("Admin access required"); }
  };

  return (
    <FormModal title={post ? "Edit story" : "New story"} eyebrow="BLOG EDITOR" close={close} width={740} testId="blog-editor">
      <form onSubmit={submit}>
        <label>Title<input data-testid="blog-title" required name="title" value={f.title} onChange={ch}/></label>
        <label>Slug (auto if empty)<input data-testid="blog-slug" name="slug" value={f.slug} onChange={ch}/></label>
        <label>Cover image URL<input data-testid="blog-cover" name="cover_image" value={f.cover_image} onChange={ch}/></label>
        <label>Excerpt<textarea data-testid="blog-excerpt" name="excerpt" value={f.excerpt} onChange={ch} rows={2}/></label>
        <label>Body (HTML supported)<textarea data-testid="blog-body" name="body" value={f.body} onChange={ch} rows={10}/></label>
        <label>Tags (comma-separated)<input data-testid="blog-tags" name="tags" value={Array.isArray(f.tags) ? f.tags.join(", ") : f.tags} onChange={ch}/></label>
        <label className="checkbox-inline">
          <input data-testid="blog-published" type="checkbox" name="published" checked={!!f.published} onChange={ch}/> Publish immediately
        </label>
        <button data-testid="blog-save" className="coral-button full" type="submit"><Save size={14}/> {post ? "Save changes" : "Publish story"}</button>
      </form>
    </FormModal>
  );
}

export function BlogAdminPanel({notify}) {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newPost, setNewPost] = useState(false);

  const load = () => apiGet("/blog?all=1").then(setPosts).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await apiDelete(`/blog/${p.post_id}`); setPosts(x => x.filter(y => y.post_id !== p.post_id)); notify("Post deleted"); }
    catch { notify("Admin access required"); }
  };

  return (
    <section className="admin-panel wide" data-testid="admin-blog">
      <div className="admin-panel-head">
        <div>
          <div className="eyebrow">THE JOURNAL</div>
          <h2>Blog posts ({posts.length})</h2>
        </div>
        <button data-testid="admin-blog-new" className="coral-button sm" onClick={() => setNewPost(true)}><Plus size={13}/> New post</button>
      </div>
      {posts.length === 0 ? (
        <p className="admin-help">No posts yet. Publish your first story to launch the journal.</p>
      ) : posts.map(p => (
        <div key={p.post_id} className="admin-row" data-testid={`admin-blog-row-${p.post_id}`}>
          <div>
            <strong>{p.title} {!p.published && <span className="pill ghost" style={{marginLeft:8}}>DRAFT</span>}</strong>
            <small>{p.author} · {new Date(p.created_at).toLocaleDateString()} · /{p.slug}</small>
            <p className="admin-row-desc">{p.excerpt}</p>
          </div>
          <button data-testid={`admin-blog-edit-${p.post_id}`} className="outline-button sm" onClick={async () => setEditing(await apiGet(`/blog/${p.slug}`))}><Edit3 size={13}/> Edit</button>
          <button data-testid={`admin-blog-delete-${p.post_id}`} className="outline-button sm" onClick={() => del(p)}><Trash2 size={13}/></button>
        </div>
      ))}
      {newPost && <BlogEditor notify={notify} close={() => setNewPost(false)} onSaved={() => load()}/>}
      {editing && <BlogEditor notify={notify} post={editing} close={() => setEditing(null)} onSaved={() => load()}/>}
    </section>
  );
}
